import { useEffect, useState } from 'react';
import type {
  CatalogImportConfirmResult,
  CatalogImportPreviewResult,
  CatalogImportResolution,
} from '@neuroedge/shared';
import { Button, FileDropzone, Modal, useToast } from '../../components/ui';
import {
  useCatalogImportJob,
  useConfirmCatalogImport,
  useCreateCatalogImportJob,
} from '../../api/products-services';

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.pdf'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function isAllowedFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function formatPrice(amount: number | null, currency: string): string {
  return amount === null ? '—' : `${currency} ${amount}`;
}

/**
 * AI-assisted catalog import popup: staff photograph (or scan-to-PDF) a
 * supplier price list, Gemini extracts rows and matches each against the
 * existing catalog, and this review screen marks every row Existing
 * (current → new price) or New before anything is written. Same
 * upload-then-preview-then-confirm shape as `ImportModal` (the structured
 * CSV/XLSX import) - a separate, independent entry point since the input
 * and review flow are genuinely different, but same modal pattern and the
 * same `FileDropzone` control, not a standalone page.
 */
export function AiCatalogImportModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CatalogImportPreviewResult | null>(null);
  const [resolutions, setResolutions] = useState<Map<number, string | null>>(new Map());
  const [result, setResult] = useState<CatalogImportConfirmResult | null>(null);

  // Background extraction job: the Gemini call over the uploaded file no
  // longer blocks this modal - it returns a job immediately, and this
  // component polls it (`useCatalogImportJob`) until it reaches a
  // terminal state. The job keeps running server-side even if the user
  // closes this modal (the "ongoing imports" panel on the Products &
  // Services page, plus a notification once it's done, cover that case).
  const [jobId, setJobId] = useState<string | null>(null);
  const createJob = useCreateCatalogImportJob();
  const job = useCatalogImportJob(jobId ?? undefined);
  const confirmImport = useConfirmCatalogImport();

  useEffect(() => {
    if (!job.data) return;
    if (job.data.status === 'DONE' && job.data.result) {
      setPreview(job.data.result);
      setResolutions(new Map());
    } else if (job.data.status === 'FAILED') {
      toast.error(job.data.errorMessage ?? 'Could not read this catalog file.');
    }
    if (job.data.status === 'DONE' || job.data.status === 'FAILED') setJobId(null);
  }, [job.data, toast]);

  function pickFile(candidate: File | null) {
    setFileError(null);
    setPreview(null);
    setResult(null);
    setResolutions(new Map());
    setJobId(null);
    if (!candidate) {
      setFile(null);
      return;
    }
    if (!isAllowedFile(candidate)) {
      setFileError('Only an image (jpg/png/webp/gif) or a PDF is allowed.');
      setFile(null);
      return;
    }
    if (candidate.size > MAX_FILE_BYTES) {
      setFileError('File must be 10MB or smaller.');
      setFile(null);
      return;
    }
    setFile(candidate);
  }

  async function runPreview() {
    if (!file) return;
    try {
      const createdJob = await createJob.mutateAsync(file);
      setJobId(createdJob.id);
      toast.success("Processing in the background - we'll show progress below.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start reading this file.');
    }
  }

  function setResolution(rowIndex: number, productServiceId: string | null) {
    setResolutions((prev) => new Map(prev).set(rowIndex, productServiceId));
  }

  const unresolvedCount = preview
    ? preview.rows.filter((r, i) => r.classification === 'AMBIGUOUS' && !resolutions.has(i)).length
    : 0;

  async function runConfirm() {
    if (!preview) return;
    const resolutionList: CatalogImportResolution[] = Array.from(resolutions.entries()).map(
      ([rowIndex, productServiceId]) => ({ rowIndex, productServiceId }),
    );
    try {
      const r = await confirmImport.mutateAsync({
        draftId: preview.draftId,
        input: { resolutions: resolutionList },
      });
      setResult(r);
      toast.success('Catalog import applied.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not apply this import.');
    }
  }

  function startOver() {
    setFile(null);
    setPreview(null);
    setResult(null);
    setFileError(null);
    setResolutions(new Map());
    setJobId(null);
  }

  const jobOngoing =
    jobId && job.data && (job.data.status === 'PENDING' || job.data.status === 'PROCESSING');

  return (
    <Modal title="Scan a supplier catalog" onClose={onClose} wide>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        Upload a photo or PDF of a supplier's price list - Gemini reads each row and matches it
        against your current catalog. Nothing is written until you confirm below.
      </p>

      {!preview && !result ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <FileDropzone
            accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
            file={file}
            onFileSelected={pickFile}
          />
          {fileError ? (
            <p style={{ margin: 0, color: 'var(--color-danger)' }}>{fileError}</p>
          ) : null}
          {jobOngoing ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              Reading the file in the background - you can close this and we'll notify you once it's
              ready.
            </p>
          ) : (
            <Button onClick={() => void runPreview()} disabled={!file || createJob.isPending}>
              {createJob.isPending ? 'Starting…' : 'Scan & preview'}
            </Button>
          )}
        </div>
      ) : null}

      {preview && !result ? (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span className="ne-badge">
              {preview.rows.filter((r) => r.classification === 'EXISTING').length} existing
            </span>
            <span className="ne-badge">
              {preview.rows.filter((r) => r.classification === 'NEW').length} new
            </span>
            {preview.rows.some((r) => r.classification === 'AMBIGUOUS') ? (
              <span className="ne-badge">
                {preview.rows.filter((r) => r.classification === 'AMBIGUOUS').length} ambiguous
              </span>
            ) : null}
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table className="ne-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={`${row.name}-${i}`}>
                    <td>
                      {row.name}
                      {row.sku ? (
                        <div
                          style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          SKU {row.sku}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {row.classification === 'EXISTING' ? (
                        <span className="ne-badge">Existing</span>
                      ) : row.classification === 'NEW' ? (
                        <span className="ne-badge">New</span>
                      ) : (
                        <select
                          value={resolutions.get(i) ?? ''}
                          onChange={(e) => setResolution(i, e.target.value || null)}
                        >
                          <option value="">Treat as New…</option>
                          {row.candidates.map((c) => (
                            <option key={c.productServiceId} value={c.productServiceId}>
                              Match: {c.name} ({formatPrice(c.priceAmount, c.currency)})
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {row.classification === 'EXISTING' ? (
                        <span
                          style={
                            row.existingPriceAmount !== row.newPriceAmount
                              ? { color: 'var(--color-warning, orange)', fontWeight: 600 }
                              : undefined
                          }
                        >
                          {formatPrice(row.existingPriceAmount, row.currency)} →{' '}
                          {formatPrice(row.newPriceAmount, row.currency)}
                        </span>
                      ) : (
                        formatPrice(row.newPriceAmount, row.currency)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {unresolvedCount > 0 ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              {unresolvedCount} ambiguous row(s) still need a choice above.
            </p>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              onClick={() => void runConfirm()}
              disabled={unresolvedCount > 0 || confirmImport.isPending}
            >
              {confirmImport.isPending ? 'Applying…' : 'Confirm import'}
            </Button>
            <Button variant="secondary" onClick={startOver} disabled={confirmImport.isPending}>
              Choose a different file
            </Button>
          </div>
        </div>
      ) : null}

      {result ? (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span className="ne-badge">{result.created} created</span>
            <span className="ne-badge">{result.updated} price updated</span>
            <span className="ne-badge">{result.skipped} skipped</span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button onClick={onClose}>Done</Button>
            <Button variant="secondary" onClick={startOver}>
              Scan another file
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
